import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { type FC, useState } from "react";

import type { PairwiseDifference } from "@api/rest/types-and-hooks";
import {
  DIFFERENCE_IMPACTS,
  DifferenceEffect,
  DifferenceSeverity,
  PAIRWISE_DIFFERENCE_LABELS,
  PAIRWISE_VERDICT_CHIP_LABELS,
  PAIRWISE_VERDICT_LABELS,
  PAIRWISE_VERDICT_RULES,
  PAIRWISE_VERDICT_TONES,
  type PairwiseVerdictValue,
} from "@components/ModelTesting/constants";

/** Side-by-side judge verdict for one slot. ``judged`` prefixes "Judged"
 *  for the review list's reason column. */
export const PairwiseVerdictChip: FC<{
  verdict: PairwiseVerdictValue;
  summary?: string | null;
  judged?: boolean;
}> = ({ verdict, summary, judged = false }) => (
  <span
    className={`inline-block whitespace-nowrap rounded-md px-2.5 py-1 text-sm font-semibold ${PAIRWISE_VERDICT_TONES[verdict]}`}
    title={summary ?? undefined}
  >
    {judged ? `Judged ${PAIRWISE_VERDICT_LABELS[verdict].toLowerCase()}` : PAIRWISE_VERDICT_CHIP_LABELS[verdict]}
  </span>
);

const impactOf = (d: PairwiseDifference) => {
  const material = d.severity === DifferenceSeverity.Material;
  if (d.effect === DifferenceEffect.Worse)
    return material ? DIFFERENCE_IMPACTS.materialLoss : DIFFERENCE_IMPACTS.minorLoss;
  if (d.effect === DifferenceEffect.Better)
    return material ? DIFFERENCE_IMPACTS.materialGain : DIFFERENCE_IMPACTS.minorGain;
  return null;
};

// Material losses drive a worse verdict, so they lead.
const IMPACT_ORDER: readonly unknown[] = [
  DIFFERENCE_IMPACTS.materialLoss,
  DIFFERENCE_IMPACTS.minorLoss,
  DIFFERENCE_IMPACTS.materialGain,
  DIFFERENCE_IMPACTS.minorGain,
];
const impactRank = (d: PairwiseDifference) => {
  const index = IMPACT_ORDER.indexOf(impactOf(d));
  return index === -1 ? IMPACT_ORDER.length : index;
};

/** The judge's summary, with two or more differences behind a toggle, as an
 *  annotation on the candidate column; the rule takes the verdict's colour.
 *  Each card names its impact; material losses come first. */
export const PairwiseDifferences: FC<{
  verdict: PairwiseVerdictValue;
  summary: string | null;
  differences: PairwiseDifference[];
}> = ({ verdict, summary, differences }) => {
  const [open, setOpen] = useState(false);
  if (!summary && differences.length === 0) return null;
  // A single difference shows inline; a toggle only pays off for a list.
  const collapsible = differences.length > 1;
  const shown = open || !collapsible;
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;
  const ordered = [...differences].sort((a, b) => impactRank(a) - impactRank(b));
  const materialLosses = differences.filter((d) => impactOf(d) === DIFFERENCE_IMPACTS.materialLoss).length;
  return (
    <div className={`mb-3 mt-1 border-l-2 pl-3 text-sm ${PAIRWISE_VERDICT_RULES[verdict]}`}>
      {summary ? <p className="text-text-primary">{summary}</p> : null}
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="mt-1.5 flex cursor-pointer items-center gap-1 text-xs font-medium text-text-secondary hover:text-text-primary"
        >
          <Chevron className="size-3 shrink-0 text-text-tertiary" />
          {differences.length} differences
          {materialLosses > 0 ? ` · ${materialLosses} material ${materialLosses === 1 ? "loss" : "losses"}` : ""}
        </button>
      ) : null}
      {shown && differences.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-2">
          {ordered.map((d, i) => {
            const impact = impactOf(d);
            return (
              <li key={i} className="rounded-md border border-border-subtle bg-bg-surface p-3 shadow-sm">
                <div className="flex items-start justify-between gap-x-3">
                  <p className="text-text-secondary">
                    <span className="font-medium text-text-primary">
                      {PAIRWISE_DIFFERENCE_LABELS[d.kind] ?? d.kind}:
                    </span>{" "}
                    {d.explanation}
                  </p>
                  {impact ? (
                    <span
                      className={`shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ${impact.tone}`}
                    >
                      {impact.label}
                    </span>
                  ) : null}
                </div>
                {d.current_quote || d.candidate_quote ? (
                  <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                    {d.current_quote ? (
                      <>
                        <dt className="text-text-tertiary">Current</dt>
                        <dd className="text-text-secondary">
                          <q>{d.current_quote}</q>
                        </dd>
                      </>
                    ) : null}
                    {d.candidate_quote ? (
                      <>
                        <dt className="text-text-tertiary">Candidate</dt>
                        <dd className="text-text-secondary">
                          <q>{d.candidate_quote}</q>
                        </dd>
                      </>
                    ) : null}
                  </dl>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};
